import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Award, ChevronDown, Clock, ShieldCheck, SlidersHorizontal } from "lucide-react";
import { IMAGES } from "@/data/site";
import { EASE } from "@/components/Reveal";

const TRUST = [
  { icon: SlidersHorizontal, label: "Customised Packages" },
  { icon: ShieldCheck, label: "Professional Drivers" },
  { icon: Clock, label: "24×7 Support" },
];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-center overflow-hidden">
      <motion.div className="absolute inset-0 scale-110" style={{ y: bgY }}>
        <img
          src={IMAGES.hero}
          alt="Taj Mahal at dawn, India"
          className="w-full h-full object-cover"
          fetchPriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F14] via-[#0B0F14]/55 to-black/45" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F14]/80 via-transparent to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-32 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE, delay: 0.15 }}
          className="animate-float inline-block"
        >
          <span data-testid="hero-badge" className="pill-badge">
            <Award size={13} />
            Trusted Journeys Since 2012
          </span>
        </motion.div>

        <h1
          data-testid="hero-title"
          className="font-sora text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] mt-8 max-w-4xl"
        >
          <MaskedLine delay={0.3}>
            Discover <span className="font-playfair italic font-medium text-[#F2C230]">Incredible</span>
          </MaskedLine>
          <MaskedLine delay={0.45}>
            India<span className="text-[#3FB6D3]">.</span> <span className="gradient-text">And Beyond.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE, delay: 0.7 }}
          className="text-base sm:text-lg text-[#F5F5F0]/75 leading-relaxed max-w-xl mt-7"
        >
          Customised tour packages, chauffeur-driven cabs and international holidays —
          designed around you and supported every hour of the road.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE, delay: 0.85 }}
          className="flex flex-wrap gap-3 mt-8"
        >
          {TRUST.map(({ icon: Icon, label }) => (
            <span key={label} className="pill-badge !text-[#F5F5F0]/85">
              <Icon size={14} className="text-[#3FB6D3]" />
              {label}
            </span>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE, delay: 1 }}
          className="flex flex-wrap gap-4 mt-10"
        >
          <Link to="/tours" data-testid="hero-cta-primary" className="btn-gold">
            Plan My Journey <ArrowRight size={16} />
          </Link>
          <Link to="/cab-taxi" data-testid="hero-cta-secondary" className="btn-outline">
            Book a Cab
          </Link>
        </motion.div>
      </motion.div>

      <motion.a
        href="#stats"
        aria-label="Scroll down"
        data-testid="hero-scroll-indicator"
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#F5F5F0]/60"
      >
        <span className="w-6 h-10 rounded-full border border-white/30 flex justify-center pt-2">
          <span className="w-1 h-2 rounded-full bg-[#F2C230] animate-scroll-dot" />
        </span>
        <ChevronDown size={14} />
      </motion.a>
    </section>
  );
};

export default Hero;
